import i18n from '../../../i18n';
import type {
  KitchenBriefingRequest,
  ServiceTimelineReservation,
} from '../types';

const CANCELLED = new Set([3, 6, 7, 14, 16]);
/** DEPARTED, PAID — already finished on the floor. */
const DONE = new Set([5, 13]);
const SLOT_MS = 15 * 60_000;
/** Match Gantt meal clamp (shift-close endTimes → usable meal length). */
const DEFAULT_MEAL_MS = 90 * 60_000;
const MAX_MEAL_MS = 4 * 60 * 60_000;
const MIN_MEAL_MS = 15 * 60_000;
const HTML_TAG_RE = /<[^>]+>/g;

export type GroupSizeBucket = {
  people: number;
  bookings: number;
  guests: number;
};

export type ArrivalSlot = {
  timeMs: number;
  guests: number;
  bookings: number;
};

function activeReservations(
  reservations: ServiceTimelineReservation[],
): ServiceTimelineReservation[] {
  return reservations.filter(
    r => r.peopleCount > 0 && !CANCELLED.has(r.state),
  );
}

/** Histogram of party sizes: N bookings of size P. */
export function groupSizeHistogram(
  reservations: ServiceTimelineReservation[],
): GroupSizeBucket[] {
  const bySize = new Map<number, number>();
  for (const r of activeReservations(reservations)) {
    bySize.set(r.peopleCount, (bySize.get(r.peopleCount) ?? 0) + 1);
  }
  return [...bySize.entries()]
    .sort((a, b) => b[0] - a[0] || b[1] - a[1])
    .map(([people, bookings]) => ({
      people,
      bookings,
      guests: people * bookings,
    }));
}

function floorToSlot(ms: number): number {
  return Math.floor(ms / SLOT_MS) * SLOT_MS;
}

/** Guests arriving (by reservation start) in 15-minute buckets. */
export function arrivalsByTimeslot(
  reservations: ServiceTimelineReservation[],
): ArrivalSlot[] {
  const bySlot = new Map<number, { guests: number; bookings: number }>();
  for (const r of activeReservations(reservations)) {
    const t = floorToSlot(r.startMs);
    const cur = bySlot.get(t) ?? { guests: 0, bookings: 0 };
    cur.guests += r.peopleCount;
    cur.bookings += 1;
    bySlot.set(t, cur);
  }
  return [...bySlot.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([timeMs, v]) => ({ timeMs, guests: v.guests, bookings: v.bookings }));
}

export function formatSlotClock(ms: number): string {
  const d = new Date(ms);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Strip residual HTML / entities from guest/staff comments for display. */
export function stripNoteHtml(raw: string): string {
  const unescaped = raw
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return unescaped.replace(HTML_TAG_RE, ' ').replace(/\s+/g, ' ').trim();
}

export function requestKindLabel(
  kind: KitchenBriefingRequest['kind'],
): string {
  if (kind === 'reservationComment') return i18n.t('chrome:briefing.staffNote');
  if (kind === 'general') return i18n.t('chrome:briefing.guestProfile');
  return i18n.t('chrome:briefing.guestNote');
}

/**
 * Effective meal end for ops UI. GraphQL endTime is often shift close;
 * clamp like the Gantt so lunch leftovers surface after midday.
 */
export function effectiveMealEndMs(r: ServiceTimelineReservation): number {
  let end = r.endMs;
  const span = end - r.startMs;
  if (span > MAX_MEAL_MS) {
    const fromDur =
      r.durationMs != null && r.durationMs > 0
        ? r.startMs + r.durationMs
        : r.startMs + DEFAULT_MEAL_MS;
    end = Math.min(
      Math.max(fromDur, r.startMs + MIN_MEAL_MS),
      r.startMs + MAX_MEAL_MS,
    );
  }
  return end;
}

/** Still open after scheduled meal end — needs departure / finish update. */
export function isOverdueUnfinished(
  r: ServiceTimelineReservation,
  nowMs: number = Date.now(),
): boolean {
  if (r.peopleCount <= 0) return false;
  if (CANCELLED.has(r.state) || DONE.has(r.state)) return false;
  return effectiveMealEndMs(r) < nowMs;
}

/** Overdue open bookings, earliest start first. */
export function overdueReservations(
  reservations: ServiceTimelineReservation[],
  nowMs: number = Date.now(),
): ServiceTimelineReservation[] {
  return reservations
    .filter(r => isOverdueUnfinished(r, nowMs))
    .sort(
      (a, b) =>
        a.startMs - b.startMs ||
        a.peopleCount - b.peopleCount ||
        a.id.localeCompare(b.id),
    );
}
